import { Suspense } from 'react';

import Config from '@config';
import {
  APP_REFRESH_ID,
  ConfirmProvider,
  ErrorBoundary,
  FeatureFlagProvider,
  KendoLocaleProvider,
  LocalizationProvider,
  NotificationProvider,
  ThemeProvider,
} from '@gasbi/shared';
import { I18nProvider, initI18nInstance } from '@gasbi/shared/common/providers';
import '@gasbi/shared/common/utils/extendYupMethods';
import '@progress/kendo-theme-default/dist/all.css';
import dayjs from 'dayjs';
import 'dayjs/locale/uk';
import { ClickScrollPlugin, OverlayScrollbars } from 'overlayscrollbars';
import 'overlayscrollbars/overlayscrollbars.css';
import { createRoot } from 'react-dom/client';
import { Provider } from 'react-redux';
import { RouterProvider } from 'react-router-dom';

import { type Flags, defaultFlags } from '@/featureFlagsConfig';
import { i18nInstanceOptions } from '@/locals';
import { AuthInitProvider, SignalRProvider } from '@/providers';

import { LicenseInfo } from '@mui/x-license';

import { GlobalLoadingScreen } from '@components/GlobalLoadingScreen';

import store from './app/store';
import { getRoutes } from './route';
import './styles/inter-font.css';

LicenseInfo.setLicenseKey(Config.VITE_MUI_LICENSE);

OverlayScrollbars.plugin(ClickScrollPlugin);

localStorage.setItem(APP_REFRESH_ID, Date.now().toString());

dayjs.locale('uk');

async function bootstrap() {
  // GSB-7591: the agents service fixture until the page runs against the live service (Frontend 3/4 removes it).
  if (import.meta.env.DEV && import.meta.env.VITE_AGENTS_MOCKS === 'true') {
    await import('@/features/agents/__mocks__');
  }
  const i18n = await initI18nInstance(i18nInstanceOptions);
  createRoot(document.getElementById('root')!).render(
    <>
      <ErrorBoundary>
        <I18nProvider instance={i18n}>
          <FeatureFlagProvider<Flags> defaultFlags={defaultFlags}>
            <Provider store={store}>
              <ThemeProvider>
                <LocalizationProvider>
                  <KendoLocaleProvider>
                    <AuthInitProvider>
                      <Suspense>
                        <NotificationProvider>
                          <ConfirmProvider>
                            <SignalRProvider>
                              <RouterProvider router={getRoutes()} />
                            </SignalRProvider>
                          </ConfirmProvider>
                        </NotificationProvider>
                      </Suspense>
                    </AuthInitProvider>
                  </KendoLocaleProvider>
                </LocalizationProvider>
              </ThemeProvider>
            </Provider>
          </FeatureFlagProvider>
        </I18nProvider>
      </ErrorBoundary>
      <GlobalLoadingScreen />
    </>,
  );
}

bootstrap();

